// Settings (brief section 8.8): company profile (onboarding), AI (on/off, kill switch, model,
// budgets), members, and the GitHub repositories the assistants may read (decision 20). Leads change
// the first three; members read them. Repositories are chosen by an administrator, for every
// workspace at once.
import { useEffect, useMemo, useState } from 'react';
import { useContextWorkspace } from '../components/Layout';
import { ErrorBox, Field, Loading, NotYet, PageHead } from '../components/ui';
import { api, errorText } from '../lib/api';
import { day, usd, when } from '../lib/format';
import { useSession } from '../lib/session';
import type { AdminRepos, Member, Profile, RepoInfo, WorkspaceDetail, WorkspaceRepos } from '../lib/types';
import { useApi } from '../lib/useApi';

const PROFILE: [keyof Profile, string, string][] = [
  ['display_name', 'Company name', ''],
  ['industry', 'Industry', 'What field the company works in'],
  ['description', 'What the company does', 'Two or three sentences'],
  ['offer', 'Products and services', ''],
  ['customers', 'Customers', 'Who buys, and how they find you'],
  ['goals', 'Goals for the next months', ''],
  ['priorities', 'Current priorities', ''],
  ['workflows', 'How work flows', 'From first contact to delivery'],
  ['constraints', 'Constraints', 'Capacity, budget, deadlines, things to avoid'],
];

export function Settings() {
  const ws = useContextWorkspace()!;
  const { me } = useSession();
  const detail = useApi<WorkspaceDetail>(`/api/w/${ws.slug}`);
  const lead = ws.role === 'lead';
  return (
    <>
      <PageHead title="Settings" sub={lead ? `You are a lead of ${ws.name}.` : `Only a lead of ${ws.name} can change these.`} />
      {detail.error && <ErrorBox error={detail.error} retry={detail.reload} />}
      {!detail.data && !detail.error && <Loading />}
      {detail.data && (
        <>
          <ProfileSection d={detail.data} lead={lead} onSaved={detail.reload} />
          <AiSection d={detail.data} lead={lead} onSaved={detail.setData} />
          <MembersSection d={detail.data} lead={lead} onSaved={detail.reload} />
          <RepositoriesSection slug={ws.slug} name={ws.name} />
          {me.is_admin && <AdminReposSection />}
          <section className="panel">
            <h2>Connections, sharing and retention</h2>
            <NotYet title="Not built yet.">
              E-mail and calendar connections (personal by default, read-only first) arrive in milestone 3; reviewed sharing between
              workspaces in milestone 2; retention and recurring briefings in milestone 4.
            </NotYet>
          </section>
        </>
      )}
    </>
  );
}

function ProfileSection({ d, lead, onSaved }: { d: WorkspaceDetail; lead: boolean; onSaved: () => void }) {
  const { refresh } = useSession();
  const [form, setForm] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  useEffect(() => {
    const f: Record<string, string> = {};
    PROFILE.forEach(([k]) => (f[k] = (d.profile[k] as string | null) ?? ''));
    setForm(f);
  }, [d.profile]);

  async function save(complete: boolean) {
    setMsg({});
    try {
      await api(`/api/w/${d.slug}/profile`, { method: 'PUT', body: { ...form, complete } });
      setMsg({ ok: complete ? 'Onboarding completed.' : 'Saved.' });
      onSaved();
      await refresh();
    } catch (e) {
      setMsg({ err: errorText(e) });
    }
  }

  const done = Boolean(d.profile.onboarding_completed_at);
  return (
    <section className="panel">
      <h2>Company profile</h2>
      <p className="muted small">
        {done
          ? `Onboarding completed on ${day(d.profile.onboarding_completed_at)}. The assistants use this profile; empty fields stay unknown.`
          : 'Onboarding: tell the assistants about the company. Nothing here is guessed; empty fields stay unknown.'}
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save(false);
        }}
      >
        <div className="grid2">
          {PROFILE.map(([k, label, hint]) => (
            <Field key={k} label={label} hint={hint || undefined}>
              {k === 'display_name' || k === 'industry' ? (
                <input value={form[k] ?? ''} disabled={!lead} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              ) : (
                <textarea rows={3} value={form[k] ?? ''} disabled={!lead} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              )}
            </Field>
          ))}
        </div>
        {lead && (
          <div className="row gap wrap">
            <button type="submit" className="btn">
              Save
            </button>
            {!done && (
              <button type="button" className="btn primary" onClick={() => void save(true)}>
                Save and complete onboarding
              </button>
            )}
          </div>
        )}
        {msg.ok && <p className="msg ok">{msg.ok}</p>}
        {msg.err && <p className="msg err">{msg.err}</p>}
      </form>
    </section>
  );
}

function AiSection({ d, lead, onSaved }: { d: WorkspaceDetail; lead: boolean; onSaved: (d: WorkspaceDetail) => void }) {
  const { me, refresh } = useSession();
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const [budgets, setBudgets] = useState({ run: String(d.ai.run_budget_usd), month: String(d.ai.monthly_budget_usd) });

  async function patch(body: Record<string, unknown>, ok: string) {
    setMsg({});
    try {
      onSaved(await api<WorkspaceDetail>(`/api/w/${d.slug}`, { method: 'PATCH', body }));
      setMsg({ ok });
      await refresh();
    } catch (e) {
      setMsg({ err: errorText(e) });
    }
  }

  return (
    <section className="panel">
      <h2>AI</h2>
      <p className="muted small">
        Assistants are Claude Code sessions. Each answer sends the question and the records it looks up to Anthropic; nothing is sent
        while AI is off.
      </p>
      <div className="settings-rows">
        <div className="setting">
          <div>
            <strong>AI for this workspace</strong>
            <p className="muted small">{d.ai.enabled ? 'On: assistants may read and send this workspace’s records to Claude.' : 'Off: no workspace content leaves the server.'}</p>
          </div>
          <button type="button" className="btn" disabled={!lead} onClick={() => void patch({ ai_enabled: !d.ai.enabled }, d.ai.enabled ? 'AI switched off.' : 'AI switched on.')}>
            {d.ai.enabled ? 'Switch off' : 'Switch on'}
          </button>
        </div>
        <div className="setting">
          <div>
            <strong>Kill switch</strong>
            <p className="muted small">{d.ai.paused ? 'Paused: no new answers; queued ones are cancelled; running ones stop at their next lookup.' : 'Stops all AI work in this workspace at once.'}</p>
          </div>
          <button type="button" className={`btn ${d.ai.paused ? '' : 'danger'}`} disabled={!lead} onClick={() => void patch({ ai_paused: !d.ai.paused }, d.ai.paused ? 'AI work resumed.' : 'AI work paused.')}>
            {d.ai.paused ? 'Resume' : 'Pause all AI work'}
          </button>
        </div>
        <div className="setting">
          <div>
            <strong>Default model</strong>
            <p className="muted small">Used unless a role or a conversation chooses another. Installation default: {d.ai.installation_default_model}.</p>
          </div>
          <div className="row gap wrap setting-controls">
            <select
              aria-label="Default model"
              value={d.ai.default_model ?? ''}
              disabled={!lead}
              onChange={(e) => void patch(e.target.value ? { default_model: e.target.value } : { clear_default_model: true }, 'Default model saved.')}
            >
              <option value="">installation default</option>
              {me.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Default effort"
              value={d.ai.default_effort ?? ''}
              disabled={!lead}
              onChange={(e) => void patch(e.target.value ? { default_effort: e.target.value } : { clear_default_effort: true }, 'Default effort saved.')}
            >
              <option value="">effort: default ({d.ai.installation_default_effort})</option>
              {['low', 'medium', 'high', 'xhigh', 'max'].map((x) => (
                <option key={x} value={x}>
                  effort: {x}
                </option>
              ))}
            </select>
          </div>
        </div>
        <form
          className="setting"
          onSubmit={(e) => {
            e.preventDefault();
            void patch({ ai_run_budget_usd: budgets.run, ai_monthly_budget_usd: budgets.month }, 'Budgets saved.');
          }}
        >
          <div>
            <strong>Budgets</strong>
            <p className="muted small">
              {usd(d.ai.committed_this_month_usd)} committed this month. An answer stops at its ceiling and says so.
            </p>
          </div>
          <div className="row gap wrap">
            <label className="inline">
              per answer $<input type="number" step="0.10" min="0.10" value={budgets.run} disabled={!lead} onChange={(e) => setBudgets({ ...budgets, run: e.target.value })} />
            </label>
            <label className="inline">
              per month $<input type="number" step="1" min="0" value={budgets.month} disabled={!lead} onChange={(e) => setBudgets({ ...budgets, month: e.target.value })} />
            </label>
            {lead && (
              <button type="submit" className="btn small">
                Save
              </button>
            )}
          </div>
        </form>
      </div>
      {msg.ok && <p className="msg ok">{msg.ok}</p>}
      {msg.err && <p className="msg err">{msg.err}</p>}
    </section>
  );
}

function MembersSection({ d, lead, onSaved }: { d: WorkspaceDetail; lead: boolean; onSaved: () => void }) {
  const { me } = useSession();
  const [form, setForm] = useState({ email: '', name: '', role: 'member' });
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setMsg({});
    try {
      await api(`/api/w/${d.slug}/members`, { body: form });
      setMsg({ ok: `${form.email} can now sign in and sees ${d.name}'s shared records (never anyone's private drafts).` });
      setForm({ email: '', name: '', role: 'member' });
      onSaved();
    } catch (err) {
      setMsg({ err: errorText(err) });
    }
  }

  async function remove(m: Member) {
    if (!window.confirm(`Remove ${m.name} from ${d.name}? They lose access at once, including work queued for them.`)) return;
    setMsg({});
    try {
      await api(`/api/w/${d.slug}/members/${m.id}`, { method: 'DELETE' });
      onSaved();
    } catch (err) {
      setMsg({ err: errorText(err) });
    }
  }

  return (
    <section className="panel">
      <h2>Members</h2>
      <ul className="recs">
        {d.members.map((m) => (
          <li key={m.id} className="rec row between wrap">
            <span>
              <strong>{m.name}</strong> <span className="muted small">{m.email}</span> <span className="badge">{m.role}</span>
              {!m.signed_in && <span className="muted small"> · invited, not signed in yet</span>}
            </span>
            {lead && m.id !== me.user.id && (
              <button type="button" className="btn small ghost" onClick={() => void remove(m)}>
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
      {lead && (
        <form className="row gap wrap invite" onSubmit={invite} aria-label="Invite a member">
          <input required type="email" placeholder="e-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} aria-label="E-mail" />
          <input required placeholder="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} aria-label="Name" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} aria-label="Role">
            <option value="member">member</option>
            <option value="lead">lead</option>
          </select>
          <button type="submit" className="btn">
            Invite
          </button>
        </form>
      )}
      <p className="muted small">People sign in at the identity service with the invited e-mail. Joining never reveals anyone's private drafts or conversations.</p>
      {msg.ok && <p className="msg ok">{msg.ok}</p>}
      {msg.err && <p className="msg err">{msg.err}</p>}
    </section>
  );
}

function RepositoriesSection({ slug, name }: { slug: string; name: string }) {
  const r = useApi<WorkspaceRepos>(`/api/w/${slug}/repos`);
  return (
    <section className="panel" aria-labelledby="repos-title">
      <h2 id="repos-title">GitHub repositories</h2>
      <p className="muted small">
        What {name}’s assistants may read about the apps: code, deploy notes, releases, open issues and live checks. Read-only; files
        that may hold secrets are never read. An administrator chooses them.
      </p>
      {r.error && <ErrorBox error={r.error} retry={r.reload} />}
      {r.data && r.data.repos.length === 0 && <p className="muted">None yet.</p>}
      {r.data && r.data.repos.length > 0 && (
        <>
          {r.data.every && <p className="small">Every repository, including new ones ({r.data.repos.length} today).</p>}
          <ul className="recs repo-list">
            {r.data.repos.map((repo) => (
              <li key={repo.id} className="rec">
                <RepoName repo={repo} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function RepoName({ repo }: { repo: RepoInfo }) {
  return (
    <div className="repo-name">
      <a href={repo.html_url} target="_blank" rel="noopener noreferrer">
        {repo.full_name.split('/')[1] ?? repo.full_name}
      </a>
      {repo.archived && <span className="badge">archived</span>}
      {repo.gone && <span className="badge">no longer on GitHub</span>}
      {repo.description && <span className="muted small repo-desc">{repo.description}</span>}
      <span className="muted small">
        {repo.summary_error
          ? `Summary failed: ${repo.summary_error}`
          : repo.summary_at
            ? `Summary from ${when(repo.summary_at)}`
            : 'No summary yet (built by the next refresh)'}
      </span>
    </div>
  );
}

/** Short workspace labels for the switches: the first word, unless two workspaces share it. */
function shortNames(list: AdminRepos['workspaces']): Record<string, string> {
  const first = list.map((w) => w.name.split(/\s+/)[0] || w.name);
  return Object.fromEntries(list.map((w, i) => [w.id, first.filter((f) => f === first[i]).length > 1 ? w.name : first[i]]));
}

function AdminReposSection() {
  const a = useApi<AdminRepos>('/api/admin/repos');
  const [filter, setFilter] = useState('');
  const [onlyOn, setOnlyOn] = useState(false);
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const busy = a.data?.refresh && ['queued', 'running'].includes(a.data.refresh.status);

  useEffect(() => {
    if (!busy) return;
    const t = window.setInterval(() => void a.reload(), 3000);
    return () => window.clearInterval(t);
  }, [busy, a.reload]);

  const labels = useMemo(() => shortNames(a.data?.workspaces ?? []), [a.data?.workspaces]);
  const shown = useMemo(() => {
    if (!a.data) return [];
    const q = filter.trim().toLowerCase();
    return a.data.repos.filter((r) => {
      if (q && !r.full_name.toLowerCase().includes(q) && !(r.description ?? '').toLowerCase().includes(q)) return false;
      if (onlyOn) return a.data!.workspaces.some((w) => a.data!.grants[w.id]?.all || a.data!.grants[w.id]?.repo_ids.includes(r.id));
      return true;
    });
  }, [a.data, filter, onlyOn]);

  async function setGrant(workspaceId: string, repoId: string | null, enabled: boolean) {
    setMsg({});
    try {
      a.setData(await api<AdminRepos>('/api/admin/repos/grants', { method: 'PUT', body: { workspace_id: workspaceId, repo_id: repoId, enabled } }));
    } catch (e) {
      setMsg({ err: errorText(e) });
    }
  }

  async function refreshNow() {
    setMsg({});
    try {
      a.setData(await api<AdminRepos>('/api/admin/repos/refresh', { method: 'POST' }));
      setMsg({ ok: 'Refresh queued: the repositories and their summaries update in a minute or two.' });
    } catch (e) {
      setMsg({ err: errorText(e) });
    }
  }

  const last = a.data?.refresh;
  return (
    <section className="panel" aria-labelledby="admin-repos-title">
      <h2 id="admin-repos-title">GitHub repositories: administrator</h2>
      <p className="muted small">
        You choose, for every workspace, which repositories its assistants may read. A shared Joint Ventures conversation only reads
        what Joint Ventures is given. Switching off applies to the next lookup.
      </p>
      {a.error && <ErrorBox error={a.error} retry={a.reload} />}
      {!a.data && !a.error && <Loading />}
      {a.data && (
        <>
          <div className="setting">
            <div>
              <strong>Refresh</strong>
              <p className="muted small">
                {!last
                  ? 'Never refreshed: the list below is empty until the first refresh.'
                  : busy
                    ? `Refreshing since ${when(last.queued_at)}…`
                    : last.status === 'done'
                      ? `Last refreshed ${when(last.finished_at)}. Every day automatically.`
                      : `Last refresh ${last.status}: ${last.outcome ?? 'no detail'}`}
              </p>
            </div>
            <button type="button" className="btn" disabled={Boolean(busy)} onClick={() => void refreshNow()}>
              {busy ? 'Refreshing…' : 'Refresh now'}
            </button>
          </div>
          <div className="setting">
            <div>
              <strong>Every repository</strong>
              <p className="muted small">Includes repositories created later.</p>
            </div>
            <div className="row gap wrap" role="group" aria-label="Every repository">
              {a.data.workspaces.map((w) => {
                const on = Boolean(a.data!.grants[w.id]?.all);
                return (
                  <button key={w.id} type="button" className="btn small toggle" aria-pressed={on} onClick={() => void setGrant(w.id, null, !on)}>
                    {labels[w.id]}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="row gap wrap repo-filter">
            <input type="search" placeholder="Filter repositories" aria-label="Filter repositories" value={filter} onChange={(e) => setFilter(e.target.value)} />
            <label className="check">
              <input type="checkbox" checked={onlyOn} onChange={(e) => setOnlyOn(e.target.checked)} /> switched on only
            </label>
          </div>
          <p className="muted small">
            {shown.length} of {a.data.repos.length} repositories
          </p>
          <ul className="recs repo-list">
            {shown.map((r) => (
              <li key={r.id} className="rec setting">
                <RepoName repo={r} />
                <div className="row gap wrap" role="group" aria-label={`Who reads ${r.full_name}`}>
                  {a.data!.workspaces.map((w) => {
                    const every = Boolean(a.data!.grants[w.id]?.all);
                    const on = every || Boolean(a.data!.grants[w.id]?.repo_ids.includes(r.id));
                    return (
                      <button
                        key={w.id}
                        type="button"
                        className="btn small toggle"
                        aria-pressed={on}
                        disabled={every || r.gone}
                        title={every ? `${w.name} reads every repository` : undefined}
                        onClick={() => void setGrant(w.id, r.id, !on)}
                      >
                        {labels[w.id]}
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {msg.ok && <p className="msg ok">{msg.ok}</p>}
      {msg.err && <p className="msg err">{msg.err}</p>}
    </section>
  );
}
